import { useGenerateSingleArtworksPdf } from "@/api/pdf";
import { Artwork } from "@/lib/types";
import { SelectValue } from "@radix-ui/react-select";
import { PropsWithChildren, useEffect, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrintDrawer from "../print/PrintDrawer";
import { Checkbox } from "../ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import {
  ArtworkPrintSettings,
  binderTemplateFields,
  completeTemplateFields,
  simpleTemplateFields,
} from "./artworkPrintSettings";
import { SupportedLocale } from "@/i18n";

type ArtworkPrintSingleDrawerProps = PropsWithChildren<{
  artwork: Artwork;
}>;

type FormValues = {
  template: string;
  settings: ArtworkPrintSettings;
};

const ArtworkPrintSingleDrawer = ({
  artwork,
}: ArtworkPrintSingleDrawerProps) => {
  const { t, i18n } = useTranslation();

  const form = useForm<FormValues>({
    defaultValues: {
      template: "",
      settings: simpleTemplateFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: false,
        }),
        {} as ArtworkPrintSettings,
      ),
    },
  });
  const { mutateAsync: generateArtworksPdf, isPending } =
    useGenerateSingleArtworksPdf();
  const template = form.watch("template");
  const formId = useId();
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  const options = useMemo(() => {
    if (template === "simple") return simpleTemplateFields;
    if (template === "complete") return completeTemplateFields;
    if (template === "binder") return binderTemplateFields;
    return [];
  }, [template]);

  useEffect(() => {
    form.resetField("settings");
  }, [options, form]);

  const onSubmit = async (data: FormValues) => {
    const file = await generateArtworksPdf({
      lang: i18n.language as SupportedLocale,
      template: data.template,
      artworkId: artwork.id,
      settings: data.settings,
    });
    const url = URL.createObjectURL(file);
    setPreviewURL(url);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{ disabled: !template, isLoading: isPending }}
          previewUrl={previewURL}
          setPreviewUrl={setPreviewURL}
          hideText
          iconSize={18}
        >
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("printSelectModel")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="simple">{t("simple")}</SelectItem>
                      <SelectItem value="complete">{t("complete")}</SelectItem>
                      <SelectItem value="binder">{t("classeur")}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <FormLabel>{t("printOptions")}</FormLabel>
              {options.map((option) => (
                <FormField
                  key={option}
                  control={form.control}
                  name={`settings.${option}`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value as boolean}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>{t(option)}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

export default ArtworkPrintSingleDrawer;
